<template>
    <div
        class="article-option"
        :class="{ selected: isSelected, disabled: isSelectDisabled, withimage: imagePath != null }"
    >
        <div class="inner">
            <div class="name-container" ref="nameContainerElement" @click="onClickOption">
                <div :class="{ radiobox: isRadio, checkbox: !isRadio }"></div>
                <img v-if="imagePath" class="image" :src="imagePath" alt="" />
                <div class="name">
                    <div class="qty" v-if="isQuantityVisible">{{ quantity }}x</div>
                    <span>{{ getNameInLocationLanguage(option, props.showInternalNames) }}<ItemIcons :articleId="option.articleId" includeOrderedBefore :mainArticleId="props.mainArticleId" /></span>
                </div>
            </div>
            <div class="quantity" v-if="isQuantityChangeAllowed">
                <button
                    type="button"
                    class="minus qty-btn"
                    @click="decreaseQuantity"
                    :disabled="!isDecreaseEnabled"
                    :class="{ disabled: !isDecreaseEnabled }"
                >
                    <i class="fa fa-minus"></i>
                </button>
                <button
                    type="button"
                    class="plus qty-btn"
                    @click="increaseQuantity"
                    :disabled="!isIncreaseEnabled"
                    :class="{ disabled: !isIncreaseEnabled }"
                >
                    <i class="fa fa-plus"></i>
                </button>
            </div>
            <div class="price" v-if="hasPrice">{{ displayedPrice }}</div>
        </div>
        <div class="subgroups" v-if="isSelected && option.optionGroups.length">
            <ArticleOptionGroup
                v-for="optionGroup in option.optionGroups"
                :key="optionGroup.optionGroupId"
                :optionGroup="optionGroup"
                :mainArticleId="props.mainArticleId"
                :showPlusOnPrices="hasPrice"
                :showOohPrices="props.showOohPrices"
                :showInternalNames="props.showInternalNames"
                :showItemImages="props.showItemImages"
                ref="articleOptionGroupComponents"
                @changed="onOptionGroupChanged"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
    import type { ICustomerOptionGroupOption } from "@/data/model/Customer";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { Ripple } from "@/shared/ui/Ripple";
    import { toMoney } from "@/shared/utils/format";
    import { computed, onMounted, ref, watch } from "vue";

    import ItemIcons from "@/components/order/ItemIcons.vue";

    import ArticleOptionGroup from "./ArticleOptionGroup.vue";

    // define props
    const props = defineProps<{
        isRadio: boolean;
        showPlusOnPrices: boolean;
        option: ICustomerOptionGroupOption;
        // the top-level main item the option is offered in
        mainArticleId: string;
        maxPerOption?: number;
        isNewOptionAllowed: boolean;
        isMaxSumReached: boolean;
        optionGroupId: string;
        showInternalNames: boolean;
        showOohPrices: boolean;
        showItemImages: boolean;
    }>();

    // define emits
    const emit = defineEmits<{
        changed: [option: ICustomerOptionGroupOption];
        selected: [option: ICustomerOptionGroupOption];
    }>();

    // define state
    const isSelected = ref(false);
    const quantity = ref(0);

    const isDecreaseEnabled = computed(() => {
        // if item is not selected - disable
        if (!isSelected.value) return false;

        // if item quantity == minQuantity - disable
        if (quantity.value <= minQuantity.value) return false;

        return true;
    });

    const isIncreaseEnabled = computed(() => {
        if (props.isMaxSumReached) {
            return false;
        }

        if (quantity.value >= maxQuantity.value) {
            return false;
        }

        if (!props.isNewOptionAllowed && !isSelected.value) {
            return false;
        }

        return true;
    });

    // define template refs
    const nameContainerElement = ref<HTMLElement | null>(null);
    const articleOptionGroupComponents = ref<InstanceType<typeof ArticleOptionGroup>[]>([]);

    // define computed
    const option = computed(() => props.option);
    const isSelectDisabled = computed(() => {
        // select is disabled if no new option is allowed and item is not selected
        return !isSelected.value && !props.isNewOptionAllowed;
    });

    const minQuantity = computed(() => props.option.minQuantity ?? 1);

    // this row's image box is 40x40, so the 148x148 thumbnail is the right variant here
    const imagePath = computed(() => (props.showItemImages ? props.option.imagePath : undefined));

    const maxQuantity = computed(() => {
        const maxItemQuantity = props.option.maxQuantity ?? Number.MAX_SAFE_INTEGER;
        const maxPerOptionQuantity = props.maxPerOption ?? Number.MAX_SAFE_INTEGER;

        // take smaller of those 2 numbers
        return Math.min(maxItemQuantity, maxPerOptionQuantity);
    });

    // if minQuantity = maxQuantity, then hide increase/decrease btns; if minQuantity is not provided we assume minQuantity = 1
    const isQuantityChangeAllowed = computed(() => minQuantity.value != maxQuantity.value);
    const isQuantityVisible = computed(() => maxQuantity.value > 1 && quantity.value > 0);

    const price = computed(() => {
        if (props.showOohPrices && props.option.oohPrice != null) {
            return props.option.oohPrice;
        }

        return props.option.price ?? 0
    });

    const hasPrice = computed(() => price.value != 0);
    const displayedPrice = computed(() => {
        const formattedPrice = toMoney(price.value);
        if (hasPrice.value && props.showPlusOnPrices && price.value > 0) {
            return "+" + formattedPrice;
        }
        return formattedPrice;
    });

    const totalAmount = computed(() => {
        if (isSelected.value) {
            let amount = quantity.value * price.value;

            for (const articleOptionGroupComponent of articleOptionGroupComponents.value) {
                amount += quantity.value * articleOptionGroupComponent.totalPrice;
            }

            return amount;
        }
        return 0;
    });
    const isValid = computed(() => {
        for (const articleOptionGroupComponent of articleOptionGroupComponents.value) {
            if (!articleOptionGroupComponent.isValid) return false;
        }
        return true;
    });

    // define methods
    function onClickOption() {
        // if we click selected checkbox - deselect
        if (!props.isRadio && isSelected.value) {
            deselect();
            return;
        }

        // otherwise select
        if (!isSelected.value && props.isNewOptionAllowed) {
            select();
        }
    }

    function decreaseQuantity() {
        quantity.value--;
    }

    function increaseQuantity() {
        quantity.value++;
    }

    function select() {
        isSelected.value = true;
    }

    function deselect() {
        isSelected.value = false;
    }

    function getArticleWithOptions(): IArticleWithOptions {
        const entry: IArticleWithOptions = {
            quantity: quantity.value,
            articleId: props.option.articleId,
            menuCategoryId: undefined,
            optionGroupId: props.optionGroupId,
            options: getSelectedOptions(),
        };

        return entry;
    }

    function getSelectedOptions() {
        const options = [] as IArticleWithOptions[];
        for (const optionGroup of articleOptionGroupComponents.value) {
            const selectedArticlesWithOptions = optionGroup.getSelectedArticlesWithOptions();
            for (const selectedOption of selectedArticlesWithOptions) {
                options.push(selectedOption);
            }
        }
        return options;
    }

    function onOptionGroupChanged(option: ICustomerOptionGroupOption) {
        emit("changed", option);
    }

    // define callbacks
    watch(isSelected, (newIsSelected) => {
        // if is newly selected
        if (newIsSelected == true) {
            // if quantity = 0 - set to min quantity
            if (quantity.value == 0) {
                quantity.value = minQuantity.value;
            }

            // trigger selected event
            emit("selected", props.option);
        }
        // if is deselected - clear quantity
        else {
            quantity.value = 0;
        }
        emit("changed", props.option);
    });

    watch(quantity, (newQuantity) => {
        // if new quantity > 0 - item should be selected
        if (newQuantity > 0) {
            select();
        }
        // if quantity = 0 - item should not be selected
        else {
            deselect();
        }
        emit("changed", props.option);
    });

    onMounted(() => {
        Ripple.bindRippleOut($(nameContainerElement.value as any), "> .radiobox, > .checkbox");

        if (props.option.preselected) {
            select();
        }
    });

    // define watch
    watch(isSelectDisabled, (disabled) => {
        if (disabled) {
            Ripple.unbind($(nameContainerElement.value as any));
        } else {
            Ripple.bindRippleOut($(nameContainerElement.value as any), "> .radiobox, > .checkbox");
        }
    });

    // define exposed
    defineExpose({
        deselect,
        option,
        isSelected,
        quantity,
        totalAmount,
        isValid,
        getArticleWithOptions,
    });
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .article-option {
        width: 100%;

        // the image is taller than the base row, so the row grows to it and every element
        // in it aligns to the top - like the article info header of the popover
        &.withimage {
            // without it the images of two consecutive options touch
            margin-bottom: 6px;

            > .inner {
                align-items: flex-start;
                height: auto;

                > .name-container {
                    align-items: flex-start;

                    > .name {
                        line-height: 20px;
                    }
                }

                // the quantity and the price are centred in a box as high as the base
                // row; on the taller row they have to collapse onto the name's line
                > .quantity {
                    height: 20px;
                    padding: 1px 0;
                }

                > .price {
                    height: 20px;
                    line-height: 20px;
                }
            }
        }

        > .inner {
            display: flex;
            width: 100%;
            flex-direction: row;
            flex-wrap: nowrap;
            align-items: center;
            justify-content: space-between;
            height: 32px;

            > .name-container {
                flex: 1 1 auto;
                display: flex;
                align-items: center;
                cursor: pointer;

                > .radiobox {
                    width: 20px;
                    height: 20px;
                    border-radius: 10px;
                    box-sizing: border-box;
                    border: 1px solid #c9c9c9;
                    flex-grow: 0;
                    flex-shrink: 0;
                    flex-basis: 20px;
                    cursor: pointer;
                    font-size: 0;
                    text-align: center;
                    transition:
                        background-color 0.3s variables.$easeOutExpo,
                        border-color 0.3s variables.$easeOutExpo;
                }

                > .checkbox {
                    width: 20px;
                    height: 20px;
                    box-sizing: border-box;
                    border: 1px solid #c9c9c9;
                    flex-grow: 0;
                    flex-shrink: 0;
                    flex-basis: 20px;
                    cursor: pointer;
                    font-size: 0;
                    text-align: center;
                    transition: border-color 0.3s variables.$easeOutExpo;
                }

                > .image {
                    flex: 0 0 auto;
                    width: 40px;
                    height: 40px;
                    object-fit: cover;
                    border-radius: 4px;
                    margin-left: 14px;
                }

                > .name {
                    text-align: left;
                    font-size: 16px;
                    padding-left: 14px;
                    color: colors.$fontColor;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    flex: 1;
                    cursor: pointer;

                    > .qty {
                        display: inline-block;
                        vertical-align: top;
                        font-size: 16px;
                        color: colors.$green;
                        // color: var(--colorPrimary);
                        font-weight: 600;
                        padding-right: 8px;
                    }
                }
            }

            > .quantity {
                white-space: nowrap;
                flex-grow: 0;
                flex-shrink: 0;
                flex-basis: 52px;
                height: 32px;
                font-size: 0;
                padding: 7px 0;
                box-sizing: border-box;

                > .qty-btn {
                    padding: 0;
                    display: inline-block;
                    vertical-align: top;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    cursor: pointer;
                    text-align: center;
                    margin: 0 4px;

                    &.minus {
                        background-color: colors.$orange;
                    }

                    &.plus {
                        background-color: colors.$green;
                        // background-color: var(--colorPrimary);
                    }

                    &.disabled {
                        cursor: default !important;
                        background-color: #aaaaaa;
                    }

                    > .fa {
                        line-height: 17px;
                        font-size: 10px;
                        color: #fff;
                    }
                }
            }

            > .price {
                text-align: right;
                flex-grow: 0;
                flex-shrink: 0;
                flex-basis: 50px;
                height: 32px;
                line-height: 32px;
                font-size: 16px;
            }
        }

        > .subgroups {
            background-color: #f8f8f8;
            padding: 6px 26px 6px 36px;
            margin: 5px -26px 5px -26px;
        }

        &.selected {
            > .inner > .name-container {
                > .checkbox {
                    border-color: colors.$green;
                    // border-color: var(--colorPrimary);

                    &:before {
                        font-family: "Font Awesome 5 Pro";
                        font-weight: 400;
                        content: "\f00c";
                        font-size: 14px;
                        color: colors.$green;
                        // color: var(--colorPrimary);
                        display: inline-block;
                        vertical-align: top;
                        line-height: 20px;
                    }
                }

                > .radiobox {
                    border-color: colors.$green;
                    // border-color: var(--colorPrimary);
                    background-color: colors.$green;
                    // background-color: var(--colorPrimary);

                    &:before {
                        font-family: "Font Awesome 5 Pro";
                        font-weight: 400;
                        content: "\f00c";
                        font-size: 12px;
                        color: #fff;
                        display: inline-block;
                        vertical-align: top;
                        line-height: 18px;
                    }
                }
            }
        }

        &.disabled {
            > .inner {
                > .name-container {
                    cursor: default !important;

                    > .checkbox {
                        cursor: default !important;
                        border-color: #c9c9c9;
                    }

                    > .name {
                        cursor: default !important;
                        color: #c9c9c9;
                    }
                }

                > .price {
                    color: #c9c9c9;
                }
            }
        }
    }
</style>
